<template>
  <div class="editable-list">
    <div v-for="(item, index) in items" :key="index" class="editable-list__row">
      <div class="editable-list__fields">
        <slot name="row" :item="item" :index="index" />
      </div>
      <StIconButton
        v-if="!single"
        class="editable-list__remove"
        icon="xmark"
        :label="removeLabel"
        variant="danger"
        @click="emit('remove', index)"
      />
    </div>
    <StButton
      v-if="!single"
      class="editable-list__add"
      variant="secondary"
      icon="plus"
      @click="emit('add')"
    >
      {{ addLabel }}
    </StButton>
  </div>
</template>

<script setup lang="ts" generic="T">
// Shared scaffolding for the editor's repeatable form sections (abilities,
// scoring rules, …): a stack of sunken row cards, each with a top-right remove
// button, plus an "add" button. The row's fields are provided via the #row slot
// so each form keeps its own layout; this component owns the card chrome and the
// add/remove affordances so they stay consistent (and fixed in one place).
import { StButton, StIconButton } from './ui/index.js';

defineProps<{
  items: T[];
  addLabel: string;
  removeLabel: string;
  // Single-item mode: hide the add and remove affordances (the list is fixed at
  // one row, e.g. a battle formation's lone ability).
  single?: boolean;
}>();

const emit = defineEmits<{
  add: [];
  remove: [index: number];
}>();

defineSlots<{
  row(props: { item: T; index: number }): unknown;
}>();
</script>

<style scoped>
.editable-list {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}
.editable-list__row {
  display: flex;
  align-items: flex-start;
  gap: var(--st-space-2);
  padding: var(--st-space-3);
  background: var(--st-surface-sunken);
  border-radius: var(--st-radius-md);
  border: 1px solid var(--st-border);
}
.editable-list__fields {
  flex: 1;
  min-width: 0;
}
.editable-list__remove {
  align-self: flex-start;
  flex-shrink: 0;
}
.editable-list__add {
  align-self: flex-start;
}
</style>
